import { useMemo } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { Bell01, CreditCard02, FileCheck02, LogOut01, User01 } from '@flipdish/ui-icons';
import type {
  NavAccountData,
  NavAccountMenuItemType,
} from '@flipdish/ui-library/components/organisms/SidebarNavigation';
import { getTranslate } from 'react-localize-redux';

import {
  propertyManagerRolePermission,
  propertyOwnerRolePermission,
} from '../../../../components/RMS/utils/permissions';
import { displayName } from '../../../../helpers/account';
import { accountRoutesConst as routes } from '../../../../routes/account.routes';
import { selectPortalUser, shouldDisplay } from '../../../../viewConfiguration';

const subscriptionPermissions: AppState['permissions'] = [
  'Owner',
  ...propertyManagerRolePermission,
  ...propertyOwnerRolePermission,
  'ManagedOwner',
  'FinanceManger',
  'FlipdishStaff',
];

type Options = {
  appId: string;
  /** Subscriptions and Invoices are app-scoped; off for global views and restricted onboarding. */
  showAppScopedLinks: boolean;
  isRestrictedOnboardingNav: boolean;
};

/** The account block at the bottom of the sidebar: identity plus the My account menu. */
export const useSidebarAccountMenu = ({
  appId,
  showAppScopedLinks,
  isRestrictedOnboardingNav,
}: Options): NavAccountData => {
  const translate = useAppSelector((state) => getTranslate(state.locale));
  const account = useAppSelector((state) => state.account);
  const portalUser = useAppSelector(selectPortalUser);
  const hasSubscriptionAccess =
    showAppScopedLinks &&
    shouldDisplay(portalUser, undefined, { legacyVisibilityPermissions: subscriptionPermissions });

  return useMemo(() => {
    const menuItems: NavAccountMenuItemType[] = [
      {
        label: translate('My_account') as string,
        icon: User01,
        href: `/${appId}/profile`,
      },
    ];

    if (hasSubscriptionAccess) {
      menuItems.push(
        {
          label: translate('Subscriptions') as string,
          icon: CreditCard02,
          href: `/${appId}/billing/org-subscriptions/subscriptions`,
        },
        {
          label: translate('Invoices') as string,
          icon: FileCheck02,
          href: `/${appId}/finance/invoices`,
        }
      );
    }

    if (!isRestrictedOnboardingNav) {
      menuItems.push({
        label: translate('Communications') as string,
        icon: Bell01,
        href: `/${appId}/communication-preferences/user`,
      });
    }

    return {
      account: {
        id: String(account?.AccountId ?? ''),
        name: displayName(account),
        email: account?.Email || '',
        avatar: '',
        status: 'online' as const,
      },
      menuItems,
      bottomMenuItems: [
        {
          label: translate('Logout') as string,
          icon: LogOut01,
          href: routes.Logout,
        },
      ],
    };
  }, [account, translate, appId, hasSubscriptionAccess, isRestrictedOnboardingNav]);
};
